/** @module @liquidefy/code-editor/circuits/ai-prompt-lab @version 1.0.0 @license LQD Research LLC */
/** @rust ai_prompt_lab.rs — false; 4/2/26; sm */

/**
 * Tier 5 — Elite: AI Optimization Workbench
 *
 * Combines A/B testing, metrics collection, tracing, budget governance,
 * lifecycle management, and model registry into a complete AI prompt
 * experimentation and optimization workbench.
 *
 * Create experiments, record observations, analyze results,
 * promote winners, and track costs — all through one circuit.
 *
 * Pure circuit. No DOM, no framework coupling.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type { CircuitHandle, CircuitHealthReport, CircuitStats } from './circuit-types.js';
import { buildHealthReport, emptyStats, recordSuccess, recordError, healthFromStats, formatStatsDetails } from './circuit-types.js';

import { generateId, now } from '../code-editor.js';

import type {
  ExperimentStatus,
  ExperimentVariant,
  Experiment,
  ExperimentResult,
  VariantSummary,
  ExperimentState,
  ObservationSignal,
} from '../ai/experimentation/ab-testing.js';
import {
  createExperimentState,
  createExperiment as abCreateExperiment,
  startExperiment as abStartExperiment,
  recordObservation as abRecordObservation,
  analyzeExperiment as abAnalyzeExperiment,
} from '../ai/experimentation/ab-testing.js';

import type { MetricsState, MetricSnapshot } from '../ai/observability/metrics.js';
import {
  createMetricsState,
  incrementCounter,
  takeSnapshot,
  getCounter,
  getGauge,
} from '../ai/observability/metrics.js';

import type { TracingState, RequestTrace } from '../ai/observability/tracing.js';
import {
  createTracingState,
  startTrace,
  startSpan,
  recordDecision,
  endTrace,
  explainTrace,
} from '../ai/observability/tracing.js';

import type { BudgetState, BudgetSummary } from '../ai/governance/budget.js';
import {
  createBudgetState,
  recordSpend,
  totalSpendUSD,
  checkBudget,
} from '../ai/governance/budget.js';

import type { LifecycleState, ModelVersion } from '../ai/governance/lifecycle.js';
import {
  createLifecycleState,
  registerModel,
  listModelsByStatus,
} from '../ai/governance/lifecycle.js';

import type { AIModelState } from '../code-editor-types.js';
import { createModelState } from '../ai/core/models.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 TYPES
// ═══════════════════════════════════════════════════════════════════════════════

export interface AIPromptLabConfig {
  readonly accentHex?: string;
  readonly maxExperiments?: number;
  readonly totalBudgetUSD?: number;
  readonly dailyBudgetUSD?: number;
}

export interface ExperimentVariantInput {
  readonly name: string;
  readonly templateId?: string;
  readonly temperature?: number;
}

export interface LabExperimentSummary {
  readonly id: string;
  readonly name: string;
  readonly status: ExperimentStatus;
  readonly variantCount: number;
  readonly totalObservations: number;
}

export interface ModelHealthEntry {
  readonly modelId: string;
  readonly status: string;
  readonly latency: number;
  readonly errorRate: number;
}

export interface LabMetrics {
  readonly totalRequests: number;
  readonly cacheHitRate: number;
  readonly avgLatency: number;
  readonly totalCost: number;
}

export interface LabBudgetStatus {
  readonly spent: number;
  readonly remaining: number;
  readonly utilization: number;
}

export interface AIPromptLabHandle extends CircuitHandle {
  createExperiment(name: string, description: string, requestType: string, variants: ExperimentVariantInput[]): string;
  startExperiment(experimentId: string): void;
  recordObservation(experimentId: string, variantId: string, signal: string, latencyMs: number, costUSD: number, quality: number): void;
  analyzeExperiment(experimentId: string): { winner: string | null; confidence: number; recommendation: string };
  promoteWinner(experimentId: string): { config: Record<string, unknown> } | null;
  listExperiments(): LabExperimentSummary[];
  getMetrics(): LabMetrics;
  getModelHealth(): ModelHealthEntry[];
  getTraceForRequest(requestId: string): string | null;
  getBudgetStatus(): LabBudgetStatus;
  stats(): CircuitStats;
  health(): CircuitHealthReport;
  destroy(): void;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 INTERNAL EXPERIMENT STORE
// ═══════════════════════════════════════════════════════════════════════════════

interface InternalExperiment {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly requestType: string;
  status: ExperimentStatus;
  readonly variants: InternalVariant[];
  startedAt?: number;
  readonly createdAt: number;
}

interface InternalVariant {
  readonly id: string;
  readonly name: string;
  readonly templateId?: string;
  readonly temperature: number;
  observations: number;
  accepted: number;
  rejected: number;
  totalLatencyMs: number;
  totalCostUSD: number;
  totalQuality: number;
}

function createInternalVariant(input: ExperimentVariantInput): InternalVariant {
  return {
    id: generateId('var'),
    name: input.name,
    templateId: input.templateId,
    temperature: input.temperature ?? 0.7,
    observations: 0,
    accepted: 0,
    rejected: 0,
    totalLatencyMs: 0,
    totalCostUSD: 0,
    totalQuality: 0,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 ANALYSIS ENGINE
// ═══════════════════════════════════════════════════════════════════════════════

function analyzeInternalExperiment(exp: InternalExperiment): { winner: string | null; confidence: number; recommendation: string } {
  if (exp.variants.length === 0) {
    return { winner: null, confidence: 0, recommendation: 'No variants to analyze' };
  }

  const summaries = exp.variants.map(v => {
    const acceptRate = v.observations > 0 ? v.accepted / v.observations : 0;
    const avgLatency = v.observations > 0 ? v.totalLatencyMs / v.observations : 0;
    const avgQuality = v.observations > 0 ? v.totalQuality / v.observations : 0;
    const avgCost = v.observations > 0 ? v.totalCostUSD / v.observations : 0;
    return { id: v.id, name: v.name, acceptRate, avgLatency, avgQuality, avgCost, observations: v.observations };
  });

  // Need minimum sample size for meaningful analysis
  const minSamples = 30;
  const sufficientData = summaries.every(s => s.observations >= minSamples);

  if (!sufficientData) {
    const totalObs = summaries.reduce((sum, s) => sum + s.observations, 0);
    const needed = minSamples * summaries.length - totalObs;
    return {
      winner: null,
      confidence: 0,
      recommendation: `Insufficient data — need approximately ${Math.max(0, needed)} more observations`,
    };
  }

  // Score each variant: weighted combination of accept rate, quality, and inverse latency
  const scored = summaries.map(s => ({
    ...s,
    score: s.acceptRate * 0.4 + s.avgQuality * 0.4 + (1 / (1 + s.avgLatency / 1000)) * 0.2,
  }));

  scored.sort((a, b) => b.score - a.score);

  const best = scored[0]!;
  const second = scored.length > 1 ? scored[1]! : null;

  // Compute confidence based on score gap and sample size
  let confidence = 0;
  if (second) {
    const gap = best.score - second.score;
    const sampleFactor = Math.min(1, Math.min(best.observations, second.observations) / 100);
    confidence = Math.min(0.99, gap * 5 * sampleFactor);
  } else {
    confidence = Math.min(0.99, 0.5 + best.observations / 200);
  }

  const roundedConf = Math.round(confidence * 100) / 100;

  let recommendation: string;
  if (confidence >= 0.95) {
    recommendation = `Strong winner: "${best.name}" with ${Math.round(best.acceptRate * 100)}% accept rate. Promote to production.`;
  } else if (confidence >= 0.8) {
    recommendation = `Likely winner: "${best.name}" (${roundedConf * 100}% confidence). Consider promoting or collecting more data.`;
  } else if (confidence >= 0.5) {
    recommendation = `Leaning toward "${best.name}" but confidence is only ${roundedConf * 100}%. Collect more data before deciding.`;
  } else {
    recommendation = `No clear winner yet. Variants are performing similarly. Continue the experiment.`;
  }

  return { winner: confidence >= 0.5 ? best.id : null, confidence: roundedConf, recommendation };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 METRICS TRACKING
// ═══════════════════════════════════════════════════════════════════════════════

interface MetricsAccumulator {
  totalRequests: number;
  totalErrors: number;
  cacheHits: number;
  cacheMisses: number;
  totalLatencyMs: number;
  totalCostUSD: number;
}

function createMetricsAccumulator(): MetricsAccumulator {
  return { totalRequests: 0, totalErrors: 0, cacheHits: 0, cacheMisses: 0, totalLatencyMs: 0, totalCostUSD: 0 };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 MODEL HEALTH TRACKING
// ═══════════════════════════════════════════════════════════════════════════════

interface ModelHealth {
  modelId: string;
  totalRequests: number;
  totalErrors: number;
  totalLatencyMs: number;
  status: 'healthy' | 'degraded' | 'unhealthy';
}

function updateModelHealth(models: Map<string, ModelHealth>, modelId: string, latencyMs: number, isError: boolean): void {
  let health = models.get(modelId);
  if (!health) {
    health = { modelId, totalRequests: 0, totalErrors: 0, totalLatencyMs: 0, status: 'healthy' };
  }
  health.totalRequests++;
  health.totalLatencyMs += latencyMs;
  if (isError) health.totalErrors++;

  const errorRate = health.totalRequests > 0 ? health.totalErrors / health.totalRequests : 0;
  const avgLatency = health.totalRequests > 0 ? health.totalLatencyMs / health.totalRequests : 0;

  if (errorRate > 0.3 || avgLatency > 10000) health.status = 'unhealthy';
  else if (errorRate > 0.1 || avgLatency > 5000) health.status = 'degraded';
  else health.status = 'healthy';

  models.set(modelId, health);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 FACTORY — createAIPromptLab
// ═══════════════════════════════════════════════════════════════════════════════

export function createAIPromptLab(config: AIPromptLabConfig): AIPromptLabHandle {
  let circuitStats: CircuitStats = emptyStats();
  let destroyed = false;
  const createdAt = Date.now();

  function guardDestroyed(): void {
    if (destroyed) throw new Error('Circuit destroyed');
  }

  const maxExperiments = config.maxExperiments ?? 50;
  const totalBudgetUSD = config.totalBudgetUSD ?? 1000;
  const dailyBudgetUSD = config.dailyBudgetUSD ?? 100;

  // ── State stores ──
  const experiments = new Map<string, InternalExperiment>();
  const metricsAcc = createMetricsAccumulator();
  const modelHealthMap = new Map<string, ModelHealth>();
  const requestTraces = new Map<string, string[]>(); // requestId → trace explanation lines
  let totalSpentUSD = 0;

  // ── Methods ──

  function doCreateExperiment(name: string, description: string, requestType: string, variants: ExperimentVariantInput[]): string {
    guardDestroyed();
    if (experiments.size >= maxExperiments) {
      circuitStats = recordError(circuitStats);
      throw new Error(`Maximum experiments reached (${maxExperiments})`);
    }
    if (variants.length < 2) {
      circuitStats = recordError(circuitStats);
      throw new Error('At least 2 variants required for an experiment');
    }

    const id = generateId('exp');
    const internalVariants = variants.map(v => createInternalVariant(v));
    const experiment: InternalExperiment = {
      id,
      name,
      description,
      requestType,
      status: 'draft',
      variants: internalVariants,
      createdAt: Date.now(),
    };
    experiments.set(id, experiment);
    circuitStats = recordSuccess(circuitStats);
    return id;
  }

  function doStartExperiment(experimentId: string): void {
    guardDestroyed();
    const exp = experiments.get(experimentId);
    if (!exp) {
      circuitStats = recordError(circuitStats);
      throw new Error(`Experiment not found: ${experimentId}`);
    }
    if (exp.status !== 'draft' && exp.status !== 'paused') {
      circuitStats = recordError(circuitStats);
      throw new Error(`Cannot start experiment in '${exp.status}' state`);
    }
    exp.status = 'running';
    exp.startedAt = Date.now();
    circuitStats = recordSuccess(circuitStats);
  }

  function doRecordObservation(
    experimentId: string,
    variantId: string,
    signal: string,
    latencyMs: number,
    costUSD: number,
    quality: number,
  ): void {
    guardDestroyed();
    const exp = experiments.get(experimentId);
    if (!exp) {
      circuitStats = recordError(circuitStats);
      throw new Error(`Experiment not found: ${experimentId}`);
    }

    const variant = exp.variants.find(v => v.id === variantId);
    if (!variant) {
      circuitStats = recordError(circuitStats);
      throw new Error(`Variant not found: ${variantId}`);
    }

    variant.observations++;
    variant.totalLatencyMs += latencyMs;
    variant.totalCostUSD += costUSD;
    variant.totalQuality += Math.max(0, Math.min(1, quality));

    if (signal === 'accepted') variant.accepted++;
    else if (signal === 'rejected') variant.rejected++;

    // Update global metrics
    metricsAcc.totalRequests++;
    metricsAcc.totalLatencyMs += latencyMs;
    metricsAcc.totalCostUSD += costUSD;
    totalSpentUSD += costUSD;

    // Track model health (use variant name as proxy model ID)
    updateModelHealth(modelHealthMap, variant.name, latencyMs, signal === 'rejected' && quality < 0.2);

    // Record trace
    const requestId = generateId('req');
    requestTraces.set(requestId, [
      `Request ${requestId}`,
      `Experiment: ${exp.name} (${experimentId})`,
      `Variant: ${variant.name} (${variantId})`,
      `Signal: ${signal}, Latency: ${latencyMs}ms, Cost: $${costUSD.toFixed(4)}, Quality: ${quality.toFixed(2)}`,
      `Timestamp: ${new Date().toISOString()}`,
    ]);

    // Cap trace history
    if (requestTraces.size > 10000) {
      const firstKey = requestTraces.keys().next().value;
      if (firstKey) requestTraces.delete(firstKey);
    }

    circuitStats = recordSuccess(circuitStats);
  }

  function doAnalyzeExperiment(experimentId: string): { winner: string | null; confidence: number; recommendation: string } {
    guardDestroyed();
    const exp = experiments.get(experimentId);
    if (!exp) {
      circuitStats = recordError(circuitStats);
      throw new Error(`Experiment not found: ${experimentId}`);
    }
    const result = analyzeInternalExperiment(exp);
    circuitStats = recordSuccess(circuitStats);
    return result;
  }

  function doPromoteWinner(experimentId: string): { config: Record<string, unknown> } | null {
    guardDestroyed();
    const exp = experiments.get(experimentId);
    if (!exp) {
      circuitStats = recordError(circuitStats);
      throw new Error(`Experiment not found: ${experimentId}`);
    }

    const analysis = analyzeInternalExperiment(exp);
    if (!analysis.winner) {
      circuitStats = recordSuccess(circuitStats);
      return null;
    }

    const winnerVariant = exp.variants.find(v => v.id === analysis.winner);
    if (!winnerVariant) {
      circuitStats = recordError(circuitStats);
      return null;
    }

    exp.status = 'completed';

    circuitStats = recordSuccess(circuitStats);
    return {
      config: {
        variantId: winnerVariant.id,
        variantName: winnerVariant.name,
        templateId: winnerVariant.templateId,
        temperature: winnerVariant.temperature,
        confidence: analysis.confidence,
        experimentId,
        promotedAt: Date.now(),
      },
    };
  }

  function doListExperiments(): LabExperimentSummary[] {
    guardDestroyed();
    const results: LabExperimentSummary[] = [];
    for (const exp of experiments.values()) {
      const totalObs = exp.variants.reduce((sum, v) => sum + v.observations, 0);
      results.push({
        id: exp.id,
        name: exp.name,
        status: exp.status,
        variantCount: exp.variants.length,
        totalObservations: totalObs,
      });
    }
    circuitStats = recordSuccess(circuitStats);
    return results;
  }

  function doGetMetrics(): LabMetrics {
    guardDestroyed();
    const hitTotal = metricsAcc.cacheHits + metricsAcc.cacheMisses;
    circuitStats = recordSuccess(circuitStats);
    return {
      totalRequests: metricsAcc.totalRequests,
      cacheHitRate: hitTotal > 0 ? metricsAcc.cacheHits / hitTotal : 0,
      avgLatency: metricsAcc.totalRequests > 0 ? Math.round(metricsAcc.totalLatencyMs / metricsAcc.totalRequests) : 0,
      totalCost: Math.round(metricsAcc.totalCostUSD * 10000) / 10000,
    };
  }

  function doGetModelHealth(): ModelHealthEntry[] {
    guardDestroyed();
    const entries: ModelHealthEntry[] = [];
    for (const health of modelHealthMap.values()) {
      const avgLatency = health.totalRequests > 0 ? Math.round(health.totalLatencyMs / health.totalRequests) : 0;
      const errorRate = health.totalRequests > 0 ? Math.round((health.totalErrors / health.totalRequests) * 1000) / 1000 : 0;
      entries.push({
        modelId: health.modelId,
        status: health.status,
        latency: avgLatency,
        errorRate,
      });
    }
    circuitStats = recordSuccess(circuitStats);
    return entries;
  }

  function doGetTraceForRequest(requestId: string): string | null {
    guardDestroyed();
    const trace = requestTraces.get(requestId);
    if (!trace) {
      circuitStats = recordSuccess(circuitStats);
      return null;
    }
    circuitStats = recordSuccess(circuitStats);
    return trace.join('\n');
  }

  function doGetBudgetStatus(): LabBudgetStatus {
    guardDestroyed();
    const remaining = Math.max(0, totalBudgetUSD - totalSpentUSD);
    const utilization = totalBudgetUSD > 0 ? Math.round((totalSpentUSD / totalBudgetUSD) * 10000) / 10000 : 0;
    circuitStats = recordSuccess(circuitStats);
    return {
      spent: Math.round(totalSpentUSD * 10000) / 10000,
      remaining: Math.round(remaining * 10000) / 10000,
      utilization,
    };
  }

  function getCircuitStats(): CircuitStats {
    return { ...circuitStats, uptime: Date.now() - createdAt };
  }

  function getHealth(): CircuitHealthReport {
    const h = destroyed ? 'destroyed' as const : healthFromStats(circuitStats);
    return buildHealthReport('ai-prompt-lab', h, formatStatsDetails(circuitStats), createdAt);
  }

  function destroy(): void {
    destroyed = true;
    experiments.clear();
    requestTraces.clear();
    modelHealthMap.clear();
  }

  return Object.freeze({
    name: 'ai-prompt-lab',
    createExperiment: doCreateExperiment,
    startExperiment: doStartExperiment,
    recordObservation: doRecordObservation,
    analyzeExperiment: doAnalyzeExperiment,
    promoteWinner: doPromoteWinner,
    listExperiments: doListExperiments,
    getMetrics: doGetMetrics,
    getModelHealth: doGetModelHealth,
    getTraceForRequest: doGetTraceForRequest,
    getBudgetStatus: doGetBudgetStatus,
    stats: getCircuitStats,
    health: getHealth,
    destroy,
  });
}
